import { useEffect, useState } from "react";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { WHATSAPP_URL } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";
const CONFIGURED = CLIENT_ID && !CLIENT_ID.startsWith("PON_AQUI");

export default function PayDialog({ open, onOpenChange, defaultAmount = 50, description = "Pago a Socify Marketing" }) {
  const [amount, setAmount] = useState(String(defaultAmount));
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (open) setAmount(String(defaultAmount));
  }, [open, defaultAmount]);

  const value = parseFloat(amount);
  const valid = !isNaN(value) && value >= 1 && value <= 10000;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pay-dialog" className="rounded-3xl sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
            Pagar a Socify
          </DialogTitle>
        </DialogHeader>
        <p data-testid="pay-description" className="text-sm text-slate-500">{description}</p>

        <div className="mt-2">
          <label className="mb-2 block font-mono-sig text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
            Importe acordado (€)
          </label>
          <input
            data-testid="pay-amount-input"
            type="number"
            min="1"
            max="10000"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full rounded-xl border border-slate-200 px-4 py-3.5 font-display text-2xl font-extrabold text-slate-900 outline-none focus:border-[#1D4ED8] focus:ring-4 focus:ring-[#1D4ED8]/10"
          />
        </div>

        {CONFIGURED ? (
          valid ? (
            <div className="mt-4" data-testid="paypal-buttons">
              <PayPalScriptProvider options={{ "client-id": CLIENT_ID, currency: "EUR", intent: "capture" }}>
                <PayPalButtons
                  style={{ layout: "vertical", shape: "pill", label: "pay" }}
                  forceReRender={[amount]}
                  disabled={paying}
                  createOrder={async () => {
                    setPaying(true);
                    try {
                      const { data } = await axios.post(`${API}/payments/create-order`, {
                        amount: value,
                        description,
                      });
                      return data.id;
                    } catch (e) {
                      setPaying(false);
                      toast.error("No se pudo iniciar el pago. Escríbenos por WhatsApp.");
                      throw e;
                    }
                  }}
                  onApprove={async (data) => {
                    try {
                      const res = await axios.post(`${API}/payments/capture-order`, { order_id: data.orderID });
                      if (res.data.status === "COMPLETED") {
                        toast.success(`¡Pago de ${res.data.amount} € recibido! Gracias.`);
                        onOpenChange(false);
                      } else {
                        toast.error("El pago no se completó.");
                      }
                    } catch {
                      toast.error("Error al confirmar el pago. Si se ha cobrado, escríbenos por WhatsApp.");
                    } finally {
                      setPaying(false);
                    }
                  }}
                  onError={() => {
                    setPaying(false);
                    toast.error("Error con PayPal. Inténtalo de nuevo o escríbenos por WhatsApp.");
                  }}
                />
              </PayPalScriptProvider>
            </div>
          ) : (
            <p data-testid="pay-invalid-amount" className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm font-medium text-amber-700">
              Introduce un importe válido entre 1 € y 10.000 €.
            </p>
          )
        ) : (
          <div data-testid="pay-not-configured" className="mt-4 rounded-xl bg-slate-50 px-4 py-4 text-sm text-slate-600">
            <p className="font-bold text-slate-900">El pago online se está activando.</p>
            <p className="mt-1">
              Mientras tanto, cierra el importe por WhatsApp y te paso el enlace de pago directo.
            </p>
            <a
              data-testid="pay-whatsapp-fallback"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-xs font-bold text-white transition-transform hover:scale-105"
            >
              <MessageCircle className="h-3.5 w-3.5" /> Hablar por WhatsApp
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
